import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  Typography,
  useTheme,
  alpha,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  PriorityHigh as WarningIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  FileUpload as FileUploadIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

import workflowService from '../../../services/workflowService';
import formatTemplateForImport from '../../../utils/formatTemplateForImport';

const WorkflowTemplatesPage = ({ isStaffPortal = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Toggle active status
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  
  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importError, setImportError] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [success, setSuccess] = useState('');
  
  useEffect(() => {
    fetchTemplates();
  }, []);
  
  const fetchTemplates = async () => {
    setLoading(true);
    setRefreshing(true);
    try {
      let response;
      
      // Use the appropriate service method based on user role
      if (isStaffPortal) {
        console.log('Using staff templates endpoint');
        response = await workflowService.getStaffTemplates();
      } else {
        console.log('Using admin templates endpoint');
        response = await workflowService.getAdminTemplates();
      }
      
      // Make sure we're getting the data array, checking different possible response structures
      if (response.data && Array.isArray(response.data)) {
        setTemplates(response.data);
      } else if (response.data && Array.isArray(response.data.data)) {
        setTemplates(response.data.data);
      } else {
        // If response doesn't contain an array, set an empty array
        console.error('Invalid templates data format:', response);
        setTemplates([]);
        setError('Received invalid data format from server');
      }
    } catch (err) {
      console.error('Error fetching workflow templates:', err);
      setError('Failed to load workflow templates. Please try again.');
      setTemplates([]); // Ensure templates is always an array
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const handleCreateTemplate = () => {
    navigate(isStaffPortal ? '/staff/workflows/templates/create' : '/admin/workflows/templates/create');
  };
  
  const handleEditTemplate = (templateId) => {
    navigate(isStaffPortal 
      ? `/staff/workflows/templates/edit/${templateId}` 
      : `/admin/workflows/templates/edit/${templateId}`);
  };
  
  const handleOpenDeleteDialog = (template, event) => {
    event.stopPropagation();
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };
  
  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    
    setIsDeleting(true);
    try {
      await workflowService.deleteTemplate(templateToDelete._id);
      setTemplates(templates.filter(t => t._id !== templateToDelete._id));
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(`Failed to delete template: ${err.message || 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleToggleActive = async (templateId, currentStatus, event) => {
    event.stopPropagation();
    setIsTogglingActive(true);
    try {
      const newStatus = !currentStatus;
      const response = await workflowService.updateTemplate(templateId, { 
        isActive: newStatus 
      });
      
      // Update the templates list with the updated template
      setTemplates(templates.map(t => 
        t._id === templateId ? response.data : t
      ));
    } catch (err) {
      console.error('Error toggling template status:', err);
      setError(`Failed to update template status: ${err.message || 'Unknown error'}`);
    } finally {
      setIsTogglingActive(false);
    }
  };
  
  const handleDuplicateTemplate = async (templateId, event) => {
    event.stopPropagation();
    try {
      // Get the template to duplicate
      const template = templates.find(t => t._id === templateId);
      if (!template) return;
      
      // Create a new template with similar data but different name
      const newTemplate = {
        ...template,
        name: `${template.name} (Copy)`,
        isActive: false // Set to inactive by default
      };
      
      // Remove the _id field
      delete newTemplate._id;
      
      // Format the template to ensure valid performer values
      const formattedTemplate = formatTemplateForImport(newTemplate);
      
      // Create the new template
      const response = await workflowService.createWorkflowTemplate(formattedTemplate);
      
      // Add the new template to the list
      setTemplates([...templates, response.data]);
    } catch (err) {
      console.error('Error duplicating template:', err);
      setError(`Failed to duplicate template: ${err.message || 'Unknown error'}`);
    }
  };
  
  const handleImportTemplate = () => {
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportError('');
    setImportPreview(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setImportFile(file);
    setImportError('');
    
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonContent = JSON.parse(e.target.result);
          // Basic validation
          if (!jsonContent.name || !jsonContent.steps || !Array.isArray(jsonContent.steps)) {
            setImportError('Invalid template format. The JSON file must include a name and steps array.');
            setImportPreview(null);
          } else {
            setImportPreview(jsonContent);
          }
        } catch (error) {
          setImportError('Invalid JSON file. Please provide a valid JSON template file.');
          setImportPreview(null);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleImportSubmit = async () => {
    if (!importPreview) return;
    
    setIsImporting(true);
    setImportError('');
    
    try {
      // Format the template to ensure valid performer values
      const formattedTemplate = formatTemplateForImport(importPreview);
      
      // Call API to create the template from the formatted JSON
      const response = await workflowService.createWorkflowTemplate(formattedTemplate);
      
      // Add the newly created template to the list
      setTemplates([...templates, response.data]);
      
      // Show success message and close dialog
      setSuccess('Template imported successfully');
      handleCloseImportDialog();
      
      // Refresh the template list
      fetchTemplates();
    } catch (err) {
      console.error('Error importing template:', err);
      setImportError(err.message || 'Failed to import template. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };
  
  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
      <Box sx={{ backgroundColor: alpha(theme.palette.primary.light, 0.04), minHeight: '100vh' }}>
        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 3 }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3, 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              <TimelineIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
              Workflow Templates
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage workflow templates for your organization
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(isStaffPortal ? '/staff/workflows' : '/admin/workflows')}
            >
              Back to Workflows
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={handleImportTemplate}
              sx={{ borderRadius: 1 }}
            >
              Import Template
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTemplate}
              sx={{ 
                px: 3,
                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
                }
              }}
            >
              Create Template
            </Button>
          </Box>
        </Box>
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 1 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Paper 
            elevation={0}
            sx={{ 
              p: 5, 
              textAlign: 'center', 
              borderRadius: 2,
              border: `1px dashed ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.paper, 0.7)
            }}
          >
            <TimelineIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.2), mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Workflow Templates Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
              Create your first workflow template to define structured processes for member requests.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTemplate}
              sx={{ borderRadius: 1 }}
            >
              Create Your First Template
            </Button>
          </Paper>
        ) : (
          <>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Refresh templates">
                <IconButton 
                  onClick={fetchTemplates}
                  color="primary"
                  sx={{
                    animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          
            <Grid container spacing={3}>
              {templates.map((template) => (
                <Grid item xs={12} sm={6} md={4} key={template._id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 20px rgba(0,0,0,0.12)',
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      border: template.isActive 
                        ? `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                        : `1px solid ${theme.palette.divider}`,
                    }}
                    onClick={() => handleEditTemplate(template._id)}
                  >
                    <Box 
                      sx={{ 
                        p: 2, 
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: template.isActive 
                          ? alpha(theme.palette.success.main, 0.05)
                          : 'background.paper',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {template.name}
                      </Typography>
                      <Chip 
                        label={template.isActive ? 'Active' : 'Inactive'} 
                        size="small"
                        color={template.isActive ? 'success' : 'default'}
                        icon={template.isActive ? <PlayArrowIcon fontSize="small" /> : <StopIcon fontSize="small" />}
                      />
                    </Box>
                    
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {template.description || 'No description provided'}
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Steps:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {template.steps && template.steps.map((step, index) => (
                            <Chip 
                              key={step._id || index}
                              label={step.name}
                              size="small"
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Form Fields:
                        </Typography>
                        <Typography variant="body2">
                          {template.formFields?.length || 0} fields defined
                        </Typography>
                      </Box>
                    </CardContent>
                    <Divider />
                    
                    <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title={template.isActive ? 'Deactivate template' : 'Activate template'}>
                          <Switch
                            checked={template.isActive}
                            onChange={(e) => handleToggleActive(template._id, template.isActive, e)}
                            color="success"
                            size="small"
                            disabled={isTogglingActive}
                          />
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Duplicate template">
                          <IconButton 
                            size="small"
                            onClick={(e) => handleDuplicateTemplate(template._id, e)}
                            color="primary"
                          >
                            <DuplicateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit template">
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTemplate(template._id);
                            }}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete template">
                          <IconButton 
                            size="small"
                            onClick={(e) => handleOpenDeleteDialog(template, e)}
                            color="error"
                            disabled={template.isActive}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Delete Workflow Template?
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the template "{templateToDelete?.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDeleteDialog} 
            color="primary"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTemplate} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import JSON Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileUploadIcon color="primary" />
            Import Workflow Template
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Import a workflow template from a JSON file. The file should contain a valid workflow template structure.
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <input
              accept="application/json"
              style={{ display: 'none' }}
              id="import-json-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="import-json-file">
              <Button 
                variant="outlined" 
                component="span"
                startIcon={<FileUploadIcon />}
                fullWidth
                sx={{ 
                  py: 2, 
                  borderStyle: 'dashed',
                  textTransform: 'none'
                }}
              >
                {importFile ? importFile.name : 'Click to select JSON file'}
              </Button>
            </label>
          </Box>
          
          {importError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {importError}
            </Alert>
          )}
          
          {importPreview && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Preview:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Template Name: {importPreview.name}
                </Typography>
                {importPreview.description && (
                  <Typography variant="body2" paragraph>
                    {importPreview.description}
                  </Typography>
                )}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Steps: {importPreview.steps?.length || 0}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {importPreview.steps?.map((step, index) => (
                      <Chip 
                        key={index}
                        label={step.name}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Form Fields: {importPreview.formFields?.length || 0}
                  </Typography>
                  {importPreview.formFields?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {importPreview.formFields.map((field, index) => (
                        <Chip 
                          key={index}
                          label={field.label}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleImportSubmit} 
            variant="contained" 
            color="primary"
            disabled={!importPreview || isImporting}
            startIcon={isImporting ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {isImporting ? 'Importing...' : 'Import Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WorkflowTemplatesPage;
